import { useState } from 'react'
import cx from 'classnames'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Spinner } from 'common/Spinner/Spinner'
import { ContentCategory, CONTENT_CATEGORY_CONFIG } from './Types'
import {
  BUCKET_KEY_TO_CATEGORY,
  CONTENT_CATEGORY_THUMBNAILS,
  getAvailableContentCategories,
} from 'utils/brandPlans/contentCategories'
import { useContentDiscovery } from 'hooks/queries/contentDiscovery/useContentDiscovery'
import { BucketKey } from 'api/clients/ContentDiscoveryClient'
import Styles from './ContentTypeSelector.module.scss'

interface ContentTypeSelectorProps {
  onSelect: (category: ContentCategory) => void
  isStrictBrandControl?: boolean
  discoveryMode?: boolean
}

export const ContentTypeSelector = ({
  onSelect,
  isStrictBrandControl = false,
  discoveryMode = false,
}: ContentTypeSelectorProps) => {
  const { closeModal } = useModals()
  const [selected, setSelected] = useState<ContentCategory | null>(null)
  const [selectedBucket, setSelectedBucket] = useState<BucketKey | null>(null)
  const { data: discoveryData, isLoading: isDiscoveryLoading } = useContentDiscovery()

  const categories = getAvailableContentCategories(isStrictBrandControl)

  const tryNew = discoveryData?.mix.filter((e) => e.count === 0 && e.tryable) ?? []
  const alreadyUsing = discoveryData?.mix.filter((e) => e.count > 0) ?? []

  const handleContinue = () => {
    if (discoveryMode) {
      if (!selectedBucket) return
      onSelect(BUCKET_KEY_TO_CATEGORY[selectedBucket])
    } else {
      if (!selected) return
      onSelect(selected)
    }
  }

  const isDisabled = discoveryMode ? !selectedBucket : !selected

  if (discoveryMode) {
    return (
      <>
        <Modal.Header title="What type of content do you want to create?" />
        <Modal.Content>
          {isDiscoveryLoading || !discoveryData ? (
            <Spinner />
          ) : (
            <div className={Styles.sections}>
              {tryNew.length > 0 && (
                <section className={Styles.section}>
                  <div className={Styles.sectionHeader}>
                    <Heading level={3}>Try something new</Heading>
                    <span className={Styles.sectionCount}>
                      <Text variant="secondary" color="var(--dark-80)">
                        {tryNew.length}
                      </Text>
                    </span>
                  </div>
                  <div className={Styles.discoveryGrid}>
                    {tryNew.map((entry) => {
                      const category = BUCKET_KEY_TO_CATEGORY[entry.key]
                      const config = CONTENT_CATEGORY_CONFIG[category]
                      const thumbnail = CONTENT_CATEGORY_THUMBNAILS[category]
                      const isSelected = selectedBucket === entry.key
                      return (
                        <button
                          key={entry.key}
                          type="button"
                          className={cx(Styles.discoveryCard, { [Styles.discoveryCardSelected]: isSelected })}
                          onClick={() => setSelectedBucket(entry.key)}
                        >
                          <div className={Styles.discoveryThumbnail}>
                            <img src={thumbnail} alt={config.labelSingular} className={Styles.discoveryThumbnailImg} />
                          </div>
                          <div className={Styles.cardText}>
                            <Text variant="largeList">{config.labelSingular}</Text>
                            <Text variant="metadata" color="var(--dark-40)">
                              {config.subtitle}
                            </Text>
                          </div>
                          <span className={Styles.newPill}>New for you</span>
                        </button>
                      )
                    })}
                  </div>
                </section>
              )}

              {alreadyUsing.length > 0 && (
                <section className={Styles.section}>
                  <div className={Styles.sectionHeader}>
                    <Heading level={3}>Already using</Heading>
                    <span className={Styles.sectionCount}>
                      <Text variant="secondary" color="var(--dark-80)">
                        {alreadyUsing.length}
                      </Text>
                    </span>
                  </div>
                  <div className={Styles.discoveryGrid}>
                    {alreadyUsing.map((entry) => {
                      const category = BUCKET_KEY_TO_CATEGORY[entry.key]
                      const config = CONTENT_CATEGORY_CONFIG[category]
                      const thumbnail = CONTENT_CATEGORY_THUMBNAILS[category]
                      const isSelected = selectedBucket === entry.key
                      return (
                        <button
                          key={entry.key}
                          type="button"
                          className={cx(Styles.discoveryCard, { [Styles.discoveryCardSelected]: isSelected })}
                          onClick={() => setSelectedBucket(entry.key)}
                        >
                          <div className={Styles.discoveryThumbnail}>
                            <img src={thumbnail} alt={config.labelSingular} className={Styles.discoveryThumbnailImg} />
                          </div>
                          <div className={Styles.cardText}>
                            <Text variant="largeList">{config.labelSingular}</Text>
                            <Text variant="metadata" color="var(--dark-40)">
                              {config.subtitle}
                            </Text>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </section>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Modal.FooterButton size="lg" variant="tertiary" onPress={closeModal}>
              Cancel
            </Modal.FooterButton>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Modal.FooterButton size="lg" isDisabled={isDisabled} onPress={handleContinue}>
              Continue to Post Details
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      </>
    )
  }

  return (
    <>
      <Modal.Header title="What type of content do you want to create?" />
      <Modal.Content>
        <div className={Styles.body}>
          <div className={Styles.grid}>
            {categories.map((category) => {
              const config = CONTENT_CATEGORY_CONFIG[category]
              const isSelected = selected === category
              const thumbnail = CONTENT_CATEGORY_THUMBNAILS[category]

              return (
                <button
                  key={category}
                  type="button"
                  className={cx(Styles.card, { [Styles.selected]: isSelected })}
                  onClick={() => setSelected(category)}
                >
                  <div className={Styles.thumbnail}>
                    <img className={Styles.thumbnailImage} src={thumbnail} alt={config.label} />
                  </div>
                  <div className={Styles.cardText}>
                    <Text variant="largeList">{config.label}</Text>
                    <Text variant="metadata" color="var(--dark-40)">
                      {config.subtitle}
                    </Text>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="lg" variant="tertiary" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton size="lg" isDisabled={!selected} onPress={() => selected && onSelect(selected)}>
            Continue to Post Details
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
